"use client"

import { React, useEffect, useMemo, useState } from "react"
import { useAuth0 } from "@auth0/auth0-react"
import { Button, Modal, Form, Alert } from "react-bootstrap"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faExclamationTriangle } from "@fortawesome/free-solid-svg-icons"

import "../styles/components/PostConflict.css"

export const PostConflict = ({ groupId }) => {
  const { isAuthenticated, getAccessTokenSilently } = useAuth0()
  const [showModal, setShowModal] = useState(false)
  const [title, setTitle] = useState("")
  const [description, setDescription] = useState("")
  const [conflictType, setConflictType] = useState("GroupPolicy")
  const [jurisdictionGroupId, setJurisdictionGroupId] = useState("")
  const [requiredResolverCount, setRequiredResolverCount] = useState(3)
  const [partyOneName, setPartyOneName] = useState("")
  const [partyOneGroupId, setPartyOneGroupId] = useState("")
  const [partyTwoName, setPartyTwoName] = useState("")
  const [partyTwoGroupId, setPartyTwoGroupId] = useState("")
  const [argumentsList, setArgumentsList] = useState([""])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [success, setSuccess] = useState(false)
  const [groups, setGroups] = useState([])
  const [selectedGroup, setSelectedGroup] = useState(groupId || "")
  const [groupSearch, setGroupSearch] = useState("")
  const apiUrl = process.env.REACT_APP_API_URL

  useEffect(() => {
    const fetchGroups = async () => {
      try {
        const token = await getAccessTokenSilently({
          authorizationParams: { audience: process.env.REACT_APP_API_AUDIENCE },
        })
        
        const response = await fetch(`${apiUrl}/api/GroupsTbls`, {
          headers: { Authorization: `Bearer ${token}` },
        })
        if (response.ok) {
          const data = await response.json()
          setGroups(data)
        }
      } catch (err) {
        console.error(err)
      }
    }
    fetchGroups()
  }, [apiUrl, getAccessTokenSilently])

  const filteredGroups = useMemo(() => {
    return groups.filter((g) =>
      (g.groupName ?? "").toLowerCase().includes(groupSearch.toLowerCase())
    )
  }, [groups, groupSearch])

  const handleSubmit = async (e) => {
    e.preventDefault()
    setLoading(true)
    setError(null)
    setSuccess(false)
    try {
      const token = await getAccessTokenSilently({
          authorizationParams: { audience: process.env.REACT_APP_API_AUDIENCE },
        })
      const response = await fetch(`${apiUrl}/api/conflicts`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`,"Content-Type": "application/json" },
        body: JSON.stringify({
          title,
          description,
          groupId: Number(selectedGroup),
          jurisdictionGroupId: jurisdictionGroupId ? Number(jurisdictionGroupId) : null,
          conflictType,
          requiredResolverCount: Number(requiredResolverCount),
          parties: [
            {
              partyRole: "Initiating",
              partyType: "Group",
              groupId: partyOneGroupId ? Number(partyOneGroupId) : Number(selectedGroup),
              name: partyOneName || "Initiating party",
            },
            {
              partyRole: "Responding",
              partyType: "Group",
              groupId: partyTwoGroupId ? Number(partyTwoGroupId) : null,
              name: partyTwoName || "Responding party",
            },
          ],
        }),
      })
      if (!response.ok) {
        throw new Error("Failed to submit conflict")
      }
      const conflict = await response.json()

      for (const arg of argumentsList) {
        if (!arg.trim()) continue
        await fetch(`${apiUrl}/api/conflicts/${conflict.id}/arguments`, {
          method: "POST",
          headers: {  Authorization: `Bearer ${token}`,"Content-Type": "application/json" },
          body: JSON.stringify({ content: arg }),
        })
      }

      setSuccess(true)
      setTitle("")
      setDescription("")
      setConflictType("GroupPolicy")
      setJurisdictionGroupId("")
      setRequiredResolverCount(3)
      setPartyOneName("")
      setPartyOneGroupId("")
      setPartyTwoName("")
      setPartyTwoGroupId("")
      setArgumentsList([""])
      setSelectedGroup(groupId || "")
      setGroupSearch("")
    } catch (err) {
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }

  const handleArgumentChange = (index, value) => {
    setArgumentsList((prev) =>
      prev.map((arg, i) => (i === index ? value : arg))
    )
  }

  const addArgumentField = () => {
    setArgumentsList((prev) => [...prev, ""])
  }

  const removeArgumentField = (index) => {
    setArgumentsList((prev) => prev.filter((_, i) => i !== index))
  }

  if (!isAuthenticated) return null

  return (
    <div className="mt-4">
      <Button variant="danger" onClick={() => setShowModal(true)} className="group-wide-cta is-danger">
        Report a Conflict
      </Button>
      <Modal
        show={showModal}
        onHide={() => setShowModal(false)}
        centered
        dialogClassName="conflict-modal-width"
        contentClassName="dark-modal"
      >
        <Form onSubmit={handleSubmit}>
          <Modal.Header closeButton closeVariant="white" className="border-secondary dark-modal">
            <Modal.Title className="d-flex align-items-center">
              <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />
              Report a Conflict
            </Modal.Title>
          </Modal.Header>
          <Modal.Body className="dark-modal">
            {error && <Alert variant="danger">{error}</Alert>}
            {success && <Alert variant="success">Conflict submitted!</Alert>}
            <Form.Group className="mb-3">
              <Form.Label>Group</Form.Label>
              <Form.Control
                type="text"
                placeholder="Search groups..."
                value={groupSearch}
                onChange={(e) => setGroupSearch(e.target.value)}
                className="bg-secondary text-light mb-2 w-100"
              />
              <Form.Select
                value={selectedGroup}
                onChange={(e) => setSelectedGroup(e.target.value)}
                className="bg-secondary text-light w-100"
                required
              >
                <option value="">Select a group</option>
                {filteredGroups.map((g) => (
                  <option key={g.id} value={g.id}>
                    {g.groupName}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Title</Form.Label>
              <Form.Control
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                required
                className="bg-secondary text-light w-100"
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Description</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                required
                className="bg-secondary text-light w-100"
              />
            </Form.Group>
            <div className="conflict-form-grid">
              <Form.Group className="mb-3">
                <Form.Label>Conflict type</Form.Label>
                <Form.Select
                  value={conflictType}
                  onChange={(e) => setConflictType(e.target.value)}
                  className="bg-secondary text-light w-100"
                >
                  <option value="GroupPolicy">Group policy conflict</option>
                  <option value="GroupAction">Group action conflict</option>
                  <option value="CampaignPolicy">Campaign policy conflict</option>
                  <option value="ParentPolicy">Parent policy conflict</option>
                  <option value="ParentAction">Parent action conflict</option>
                  <option value="GroupPurpose">Purpose conflict</option>
                </Form.Select>
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Resolver count</Form.Label>
                <Form.Control
                  type="number"
                  min="3"
                  step="2"
                  value={requiredResolverCount}
                  onChange={(e) => setRequiredResolverCount(e.target.value)}
                  className="bg-secondary text-light w-100"
                />
              </Form.Group>
            </div>
            <Form.Group className="mb-3">
              <Form.Label>Parent or jurisdiction group</Form.Label>
              <Form.Select
                value={jurisdictionGroupId}
                onChange={(e) => setJurisdictionGroupId(e.target.value)}
                className="bg-secondary text-light w-100"
              >
                <option value="">Use group authority for now</option>
                {groups.map((g) => (
                  <option key={g.id} value={g.id}>
                    {g.groupName}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
            <div className="conflict-party-grid">
              <Form.Group className="mb-3">
                <Form.Label>Initiating party</Form.Label>
                <Form.Control
                  value={partyOneName}
                  onChange={(e) => setPartyOneName(e.target.value)}
                  placeholder="Group, campaign, policy, or action"
                  className="bg-secondary text-light mb-2 w-100"
                />
                <Form.Select
                  value={partyOneGroupId}
                  onChange={(e) => setPartyOneGroupId(e.target.value)}
                  className="bg-secondary text-light w-100"
                >
                  <option value="">Use selected group</option>
                  {groups.map((g) => (
                    <option key={g.id} value={g.id}>
                      {g.groupName}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Responding party</Form.Label>
                <Form.Control
                  value={partyTwoName}
                  onChange={(e) => setPartyTwoName(e.target.value)}
                  placeholder="Opposing group, policy, campaign, or action"
                  className="bg-secondary text-light mb-2 w-100"
                />
                <Form.Select
                  value={partyTwoGroupId}
                  onChange={(e) => setPartyTwoGroupId(e.target.value)}
                  className="bg-secondary text-light w-100"
                >
                  <option value="">No group selected</option>
                  {groups.map((g) => (
                    <option key={g.id} value={g.id}>
                      {g.groupName}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </div>
            {argumentsList.map((arg, index) => (
              <Form.Group key={index} className="mb-3">
                <Form.Label>
                  Argument {index + 1}
                </Form.Label>
                <div className="d-flex gap-2">
                  <Form.Control
                    as="textarea"
                    rows={2}
                    value={arg}
                    onChange={(e) =>
                      handleArgumentChange(index, e.target.value)
                    }
                    className="bg-secondary text-light flex-grow-1 w-100"
                  />
                  {argumentsList.length > 1 && (
                    <Button
                      variant="danger"
                      onClick={() => removeArgumentField(index)}
                    >
                      &times;
                    </Button>
                  )}
                </div>
              </Form.Group>
            ))}
            <Button
              variant="info"
              onClick={addArgumentField}
              className="mb-3"
            >
              Add Argument
            </Button>
          </Modal.Body>
          <Modal.Footer className="border-secondary dark-modal gap-3">
            <Button
              variant="secondary"
              onClick={() => setShowModal(false)}
              className="px-4"
            >
              Close
            </Button>
            <Button type="submit" disabled={loading} variant="success" className="px-4">
              {loading ? "Submitting..." : "Submit Conflict"}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </div>
  )
}

export default PostConflict

